import { useState, useEffect, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/lib/auth-store';
import { AlertTriangle, KeyRound, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Brandmark } from '@/components/Brandmark';

/**
 * Landing page the password-reset email link points at.
 *
 * Flow:
 *   1. User clicks the link in the email → Supabase GoTrue verifies the
 *      recovery token and fires a PASSWORD_RECOVERY auth event with a
 *      one-time session attached.
 *   2. We listen for that event and surface the new-password form.
 *   3. updateUser({ password }) commits the new password on the
 *      recovery session, then we bounce to /login.
 */
export function ResetPasswordPage() {
  const { updatePassword } = useAuthStore();
  const [recovering, setRecovering] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase parses the hash (`#access_token=...&type=recovery`) on load
    // and fires PASSWORD_RECOVERY — that's our cue to render the form.
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setRecovering(true);
    });

    // Also check if we already have a recovery session from a prior load.
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) setRecovering(true);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);

    if (newPassword.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr('Passwords do not match.');
      return;
    }

    setSaving(true);
    const error = await updatePassword(newPassword);
    setSaving(false);
    if (error) {
      setErr(error);
      return;
    }

    setDone(true);
    // Sign the recovery session out and do a hard navigation to the
    // root. We must hard-navigate (not React Router) because pushState
    // preserves the URL fragment (#access_token=…&type=recovery), which
    // would cause the App's recovery-hash detector to bounce us right
    // back to this page. `location.replace('/')` wipes the fragment and
    // reloads cleanly to the login screen.
    setTimeout(async () => {
      await supabase.auth.signOut();
      window.location.replace('/');
    }, 2000);
  }

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center bg-slate-950 p-4 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(51,65,85,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(51,65,85,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-3">
          <Brandmark size={48} />
          <div>
            <h1 className="text-2xl tracking-tight text-slate-100 leading-none">
              <span className="font-black">EMS</span><span className="font-normal">PCR</span><span className="wordmark-pulse text-brand-400 font-bold">.</span>
            </h1>
            <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              Set New Password
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-md border border-slate-700 bg-slate-900/80 backdrop-blur-sm shadow-xl"
        >
          <div className="border-b border-slate-700 bg-slate-900 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <KeyRound size={12} />
            Password Recovery
          </div>

          <div className="space-y-4 p-5">
            {done ? (
              <div className="flex items-start gap-2 rounded-sm border border-green-800 bg-green-950/50 p-3 text-sm text-green-300">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Password updated</p>
                  <p className="mt-1 text-xs text-green-400/80">
                    Redirecting you to sign in…
                  </p>
                </div>
              </div>
            ) : !recovering ? (
              <div className="flex items-start gap-2 rounded-sm border border-amber-800 bg-amber-950/50 p-3 text-sm text-amber-300">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Invalid or expired reset link</p>
                  <p className="mt-1 text-xs text-amber-400/80">
                    Request a new password reset email from the sign-in page.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {err && (
                  <div className="flex items-start gap-2 rounded-sm border border-red-800 bg-red-950/50 p-3 text-sm text-red-300">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>{err}</span>
                  </div>
                )}

                <div>
                  <label htmlFor="new-password" className="data-label mb-1 block">New Password</label>
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-field"
                    placeholder="At least 8 characters"
                    required
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="confirm-password" className="data-label mb-1 block">Confirm New Password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field"
                    required
                    autoComplete="new-password"
                  />
                </div>

                <button type="submit" disabled={saving} className="btn-primary w-full">
                  {saving ? 'Updating…' : 'Set New Password'}
                </button>
              </>
            )}
          </div>
        </form>

        <div className="mt-4 flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-600">
          <ShieldCheck size={10} className="text-green-700" />
          <span>HIPAA · ePHI Encrypted · Audit Logged</span>
        </div>
      </div>
    </div>
  );
}
